import { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};

// Note private con data e ora, ordine cronologico e modifica in linea
export default function NotesSection({ contact, apply, busy }) {
  const [noteText, setNoteText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);

  const notes = [...(contact.notes || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const add = async () => {
    if (noteText.trim().length < 2) {
      toast.error("Scrivi una nota");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/contacts/${contact.id}/notes`, { text: noteText });
      apply(data);
      setNoteText("");
      toast.success("Nota salvata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (editText.trim().length < 2) {
      toast.error("Scrivi una nota");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/contacts/${contact.id}/notes/${editingId}`, { text: editText });
      apply(data);
      setEditingId(null);
      setEditText("");
      toast.success("Nota aggiornata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="notes-section">
      <div className="mt-2 flex gap-2">
        <input
          className="field !py-2 !text-[13px]"
          placeholder="Aggiungi una nota privata…"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid="contact-note-input"
        />
        <button onClick={add} disabled={saving || busy} className="btn btn-outline !min-h-[40px] !px-4 !text-[10px]" data-testid="contact-note-add">
          {saving ? <Loader2 size={12} className="animate-spin" /> : null} Aggiungi
        </button>
      </div>
      <div className="mt-3 grid gap-2">
        {notes.length === 0 && (
          <p className="text-[12px] text-[#777]" data-testid="contact-notes-empty">Nessuna nota, per ora.</p>
        )}
        {notes.map((n) => (
          <div key={n.id} className="border-l border-gold/40 bg-[#141414] px-3 py-2" data-testid={`contact-note-${n.id}`}>
            {editingId === n.id ? (
              <div className="grid gap-2">
                <textarea
                  className="field !py-2 !text-[13px]"
                  rows={3}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  data-testid={`contact-note-edit-input-${n.id}`}
                />
                <div className="flex gap-2">
                  <button onClick={saveEdit} disabled={saving} className="btn btn-gold !min-h-[32px] !px-3 !text-[10px]" data-testid={`contact-note-save-${n.id}`}>
                    Salva
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-[10px] uppercase tracking-[0.12em] text-[#929292]" data-testid={`contact-note-cancel-${n.id}`}>
                    Annulla
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-[12px] leading-relaxed text-[#dedbd4]">{n.text}</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="font-mono text-[9px] tracking-[0.1em] text-[#777]">
                    {fmt(n.created_at)}
                    {n.updated ? " · modificata" : ""}
                  </p>
                  <button
                    onClick={() => {
                      setEditingId(n.id);
                      setEditText(n.text);
                    }}
                    className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.12em] text-[#929292] hover:text-gold-light"
                    data-testid={`contact-note-edit-${n.id}`}
                  >
                    <Pencil size={10} /> Modifica
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
